import {isFeatureEnabled} from 'feature-flags';
import {
  ConfirmDialog,
  useDialog,
  openSuccessDialog,
  openDialog,
  closeCurrentDialog,
  Action,
  Actions,
  DropdownActionMenuItem,
  Tooltip,
} from 'platform/components';
import {Hide, HStack, Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {Link} from 'react-router-dom';

import {head, isNotNil, pipe, replace} from 'ramda';

import {
  SourcingVehicleListItemResponseBody,
  useAuctionBulkCreateUserComparisonMutation,
  useAuctionBulkCreateUserFavouriteMutation,
  useAuctionBulkCreateUserHiddenMutation,
  useAuctionBulkCreateUserToBuyMutation,
  useAuctionBulkDeleteUserComparisonMutation,
  useAuctionBulkDeleteUserFavouriteMutation,
  useAuctionBulkDeleteUserHiddenMutation,
  useAuctionBulkDeleteUserToBuyMutation,
  useLazySourcingGetAdServerRedirectQuery,
  useSourcingCreateUserCarvagoBuyMutation,
  useSourcingBulkCreateUserComparisonMutation,
  useSourcingBulkCreateUserFavouriteMutation,
  useSourcingBulkCreateUserHiddenMutation,
  useSourcingBulkCreateUserToBuyMutation,
  useSourcingBulkDeleteUserComparisonMutation,
  useSourcingBulkDeleteUserFavouriteMutation,
  useSourcingBulkDeleteUserHiddenMutation,
  useSourcingBulkDeleteUserToBuyMutation,
} from '@dms/api/core';
import {CountryResponseBody} from '@dms/api/shared';
import featureFlags from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {businessCaseRoutes, testIds} from '@dms/routes';
import {handleApiError, queryParams, usePermissions} from '@dms/shared';

import {
  buildArray,
  capitalize,
  composePath,
  openInNewTab,
  suffixTestId,
  TestIdProps,
  pushGtmEvent,
} from 'shared/web';

import {useCreatePriceReport} from '../../hooks/useCreatePriceReport';
import {useOpenVehicleDetail} from '../../hooks/useOpenVehicleDetail';
import {useSourcingCurrency} from '../../hooks/useSourcingCurrency';
import {useSourcingPage} from '../../hooks/useSourcingPage';
import {useSourcingTargetCountry} from '../../hooks/useSourcingTargetCountry';
import {SourcingType, useSourcingType} from '../../hooks/useSourcingType';
import {SourcingPage} from '../../types/SourcingPage';
import {getCarvagoBuyDisabledTooltipLabel} from '../../utils/getCarvagoBuyDisabledTooltipLabel';
import {getCountryAdServerName} from '../../utils/getCountryAdServerName';
import {isCarvagoBuyBlockedBySuspiciousVehicle} from '../../utils/isCarvagoBuyBlockedBySuspiciousVehicle';
import {isClassifiedsAdInactive} from '../../utils/isClassifiedsAdInactive';
import {isUnifiedSourcingEnabled} from '../../utils/isUnifiedSourcingEnabled';
import {openCarvagoTransactionWorkspace} from '../../utils/openCarvagoTransactionWorkspace';
import {AdServerRelativeFiltersDropdown} from '../AdServerRelativeFiltersDropdown/AdServerRelativeFiltersDropdown';
import {useRelativeFiltersPresetEditor} from '../RelativeFiltersPresetEditor/hooks/useRelativeFiltersPresetEditor';
import {SourcingCarvagoBuyDialog} from '../SourcingCarvagoBuyDialog/SourcingCarvagoBuyDialog';
import {SourcingCarvagoRemoveDialog} from '../SourcingCarvagoRemoveDialog/SourcingCarvagoRemoveDialog';
import {useSourcingCebiaReportAction} from '../SourcingCebiaReport/hooks/useSourcingCebiaReportAction';
import {CarvagoActionButton} from './components/CarvagoActionButton';
import {SourcingActionButton} from './components/SourcingActionButton';

interface ActionButtonsVehicleProps extends TestIdProps {
  vehicle: SourcingVehicleListItemResponseBody;
  onDetailOpen?: () => void;
}

type SourcingMenuItem = DropdownActionMenuItem & {label: string};

// eslint-disable-next-line complexity, max-lines-per-function, max-statements -- Pre-existing violation
export function ActionButtonsVehicle(props: ActionButtonsVehicleProps) {
  const hasUnifiedSurfaces = isUnifiedSourcingEnabled();
  const sourcingType = useSourcingType();
  const sourcingPage = useSourcingPage();
  const targetCountry = useSourcingTargetCountry();
  const sourcingCurrency = useSourcingCurrency();
  const [canCreatePurchaseCase, canAddVehicleToBuy] = usePermissions({
    permissionKeys: ['createBusinessCasePurchase', 'addVehicleToBuy'],
  });
  const {createPriceReport, isPriceReportCreating} = useCreatePriceReport(
    props.vehicle.sourcingVehicle.adId
  );
  const {confirmDialog: cebiaConfirmDialog, ...cebiaReportAction} = useSourcingCebiaReportAction({
    externalId:
      sourcingType === SourcingType.CLASSIFIEDS ? props.vehicle.sourcingVehicle.adId : null,
    vin: props.vehicle.sourcingVehicle.vin,
  });
  const [createSourcingFavourite] = useSourcingBulkCreateUserFavouriteMutation();
  const [deleteSourcingFavourite] = useSourcingBulkDeleteUserFavouriteMutation();
  const [createAuctionFavourite] = useAuctionBulkCreateUserFavouriteMutation();
  const [deleteAuctionFavourite] = useAuctionBulkDeleteUserFavouriteMutation();
  const [createSourcingComparison] = useSourcingBulkCreateUserComparisonMutation();
  const [deleteSourcingComparison] = useSourcingBulkDeleteUserComparisonMutation();
  const [createAuctionComparison] = useAuctionBulkCreateUserComparisonMutation();
  const [deleteAuctionComparison] = useAuctionBulkDeleteUserComparisonMutation();
  const [createSourcingHidden] = useSourcingBulkCreateUserHiddenMutation();
  const [deleteSourcingHidden] = useSourcingBulkDeleteUserHiddenMutation();
  const [createAuctionHidden] = useAuctionBulkCreateUserHiddenMutation();
  const [deleteAuctionHidden] = useAuctionBulkDeleteUserHiddenMutation();
  const [createSourcingToBuy] = useSourcingBulkCreateUserToBuyMutation();
  const [deleteSourcingToBuy] = useSourcingBulkDeleteUserToBuyMutation();
  const [createAuctionToBuy] = useAuctionBulkCreateUserToBuyMutation();
  const [deleteAuctionToBuy] = useAuctionBulkDeleteUserToBuyMutation();
  const [createSourcingCarvagoBuy] = useSourcingCreateUserCarvagoBuyMutation();
  const carvagoRemoveDialogId = `sourcing-carvago-remove-${props.vehicle.sourcingVehicle.adId}`;
  const carvagoRemoveDialogTestId = testIds.sourcing.classifieds(
    'actionButtonsVehicle-carvagoRemoveDialog'
  );
  const [getAdServerRedirect] = useLazySourcingGetAdServerRedirectQuery();
  const [isOpen, open, close] = useDialog();
  const [isCarvagoBuyOpen, openCarvagoBuy, closeCarvagoBuy] = useDialog();
  const relativeFiltersPresetEditor = useRelativeFiltersPresetEditor(props);

  const {getDetailPath, isCurrentDetailPage, rememberOrigin, openDetail} = useOpenVehicleDetail(
    props.vehicle?.sourcingVehicle?.adId,
    props.onDetailOpen
  );

  const isVehicleHidden = sourcingPage === SourcingPage.HIDDEN_PAGE || props.vehicle?.hidden;
  const isInactiveClassifieds =
    sourcingType === SourcingType.CLASSIFIEDS &&
    isClassifiedsAdInactive(props.vehicle.sourcingVehicle);
  const isCarvagoBuyBlocked = isCarvagoBuyBlockedBySuspiciousVehicle(props.vehicle.sourcingVehicle);
  const isCarvagoBuyAlreadyRequested = props.vehicle?.carvagoBuy ?? false;
  const isCarvagoRemoveAvailable = hasUnifiedSurfaces && isCarvagoBuyAlreadyRequested;
  const isCarvagoBuyDisabled = hasUnifiedSurfaces
    ? !isCarvagoBuyAlreadyRequested && (isCarvagoBuyBlocked || isInactiveClassifieds)
    : isCarvagoBuyAlreadyRequested || isCarvagoBuyBlocked || isInactiveClassifieds;
  const carvagoBuyDisabledTooltipLabel = isCarvagoRemoveAvailable
    ? undefined
    : getCarvagoBuyDisabledTooltipLabel({
        isBlocked: isCarvagoBuyBlocked,
        isAlreadyRequested: isCarvagoBuyAlreadyRequested,
        isInactive: isInactiveClassifieds,
      });
  const carvagoActionLabel = i18n.t(
    isCarvagoRemoveAvailable
      ? 'general.actions.removeFromCarvagoBuy'
      : 'entity.sourcing.vehicleList.actions.requestOnCarvago'
  );
  const countryAdServerName = getCountryAdServerName(targetCountry?.code);
  const adServerReadable = pipe(
    replace('_', '.'),
    capitalize
  )(props.vehicle.sourcingVehicle.server ?? '');

  const trackSourcingAction = (action: string) => {
    const event = `sourcing_${sourcingType}_action_completed`;
    pushGtmEvent({
      event,
      event_id: event,
      params: {
        action,
        sourcing_type: sourcingType,
        sourcing_page: sourcingPage,
        ad_server: props.vehicle.sourcingVehicle.server ?? 'unknown',
      },
    });
  };

  const callBulkAction = (
    sourcingMutation: (arg: {body: {externalIds: string[]}}) => {unwrap: () => Promise<unknown>},
    auctionMutation: (arg: {body: {externalIds: string[]}}) => {
      unwrap: () => Promise<unknown>;
    }
  ) => {
    const externalIds = [props.vehicle?.sourcingVehicle?.adId];
    return sourcingType === SourcingType.AUCTIONS
      ? auctionMutation({body: {externalIds}})
      : sourcingMutation({body: {externalIds}});
  };

  const handleHideButtonClick = () => {
    isVehicleHidden
      ? isNotNil(props.vehicle?.sourcingVehicle?.adId) &&
        callBulkAction(deleteSourcingHidden, deleteAuctionHidden)
          .unwrap()
          .then(() => trackSourcingAction('vehicle_unhidden'))
          .catch(handleApiError)
      : open();
  };

  const handleConfirmHide = () => {
    isNotNil(props.vehicle?.sourcingVehicle?.adId) &&
      callBulkAction(createSourcingHidden, createAuctionHidden)
        .unwrap()
        .then(() => trackSourcingAction('vehicle_hidden'))
        .catch(handleApiError);
  };

  const handleComparisonButtonClick = () => {
    isNotNil(props.vehicle?.sourcingVehicle?.adId) &&
      callBulkAction(
        props.vehicle?.comparison ? deleteSourcingComparison : createSourcingComparison,
        props.vehicle?.comparison ? deleteAuctionComparison : createAuctionComparison
      )
        .unwrap()
        .then(() =>
          trackSourcingAction(props.vehicle?.comparison ? 'comparison_removed' : 'comparison_added')
        )
        .catch(handleApiError);
  };

  const handleFavouriteButtonClick = () => {
    isNotNil(props.vehicle?.sourcingVehicle?.adId) &&
      callBulkAction(
        props.vehicle?.favourite ? deleteSourcingFavourite : createSourcingFavourite,
        props.vehicle?.favourite ? deleteAuctionFavourite : createAuctionFavourite
      )
        .unwrap()
        .then(() =>
          trackSourcingAction(props.vehicle?.favourite ? 'favourite_removed' : 'favourite_added')
        )
        .catch(handleApiError);
  };

  const handleShopButtonClick = () => {
    isNotNil(props.vehicle?.sourcingVehicle?.adId) &&
      callBulkAction(
        props.vehicle?.inCart ? deleteSourcingToBuy : createSourcingToBuy,
        props.vehicle?.inCart ? deleteAuctionToBuy : createAuctionToBuy
      )
        .unwrap()
        .then(() => trackSourcingAction(props.vehicle?.inCart ? 'to_buy_removed' : 'to_buy_added'))
        .catch(handleApiError);
  };

  const handleCarvagoBuyButtonClick = () => {
    match({
      hasUnifiedSurfaces,
      isDisabled: isCarvagoBuyDisabled,
      isRequested: isCarvagoBuyAlreadyRequested,
    })
      .with({isDisabled: true}, () => undefined)
      .with({hasUnifiedSurfaces: false}, () => openCarvagoBuy())
      .with({isRequested: true}, () =>
        openDialog(
          <SourcingCarvagoRemoveDialog
            externalId={props.vehicle.sourcingVehicle.adId}
            dialogId={carvagoRemoveDialogId}
            onRemoved={() => trackSourcingAction('carvago_buy_removed')}
            data-testid={carvagoRemoveDialogTestId}
          />,
          {
            id: carvagoRemoveDialogId,
            title: i18n.t('general.actions.removeFromCarvagoBuy'),
            size: 'small',
            'data-testid': carvagoRemoveDialogTestId,
          }
        )
      )
      .otherwise(() => openCarvagoBuy());
  };

  const handleConfirmCarvagoBuy = () => {
    if (!isNotNil(props.vehicle?.sourcingVehicle?.adId)) {
      return;
    }
    createSourcingCarvagoBuy({
      createUserCarvagoBuyRequestBody: {externalId: props.vehicle.sourcingVehicle.adId},
    })
      .unwrap()
      .then(() => {
        trackSourcingAction('carvago_buy_requested');
        openSuccessDialog({
          id: 'sourcing-carvago-buy-success-dialog',
          heading: i18n.t('entity.sourcing.notifications.carvagoBuySentTitle'),
          subheading: i18n.t('entity.sourcing.notifications.carvagoBuySentToCarvago', {count: 1}),
          secondaryButtonLabel: i18n.t('general.actions.trackRequest'),
          onSecondaryButtonClick: openCarvagoTransactionWorkspace,
          buttonLabel: i18n.t('general.actions.close'),
          onClose: closeCurrentDialog,
          'data-testid': testIds.sourcing.classifieds(
            'actionButtonsVehicle-carvagoBuySuccessDialog'
          ),
        });
        openCarvagoTransactionWorkspace();
      })
      .catch(handleApiError);
    closeCarvagoBuy();
  };

  const handleAdServerOpen = (
    externalId: string,
    serverName: string,
    targetCountry: CountryResponseBody | null,
    relativeFilterId?: string
  ) =>
    getAdServerRedirect({
      externalId,
      serverName,
      sellerCountry: targetCountry?.code,
      currency: sourcingCurrency?.code,
      relativeFilterId,
    })
      .unwrap()
      .then((response) => {
        trackSourcingAction(`ad_server_opened_${serverName}`);
        openInNewTab(response?.url);
      })
      .catch(handleApiError);

  const createAdServerSearchMenuItem = ({
    label,
    serverName,
    testId,
  }: {
    label: string;
    serverName: string;
    testId: string;
  }): SourcingMenuItem => ({
    leftIcon: 'content/filter_list',
    label,
    ...(isFeatureEnabled(featureFlags.SOURCING_SAVED_FILTERS)
      ? {
          submenuContent: (closeDropdown) => (
            <AdServerRelativeFiltersDropdown
              onOpenWithPreset={(presetId) =>
                handleAdServerOpen(
                  props.vehicle.sourcingVehicle.adId,
                  serverName,
                  targetCountry,
                  presetId
                )
              }
              onCreatePreset={relativeFiltersPresetEditor.openCreate}
              onEditPreset={relativeFiltersPresetEditor.openEdit}
              onClose={closeDropdown}
              data-testid={suffixTestId(testId, props)}
            />
          ),
        }
      : {
          onClick: () =>
            handleAdServerOpen(props.vehicle.sourcingVehicle.adId, serverName, targetCountry),
        }),
  });

  const menuItems = buildArray<SourcingMenuItem>()
    .when(
      sourcingType === SourcingType.CLASSIFIEDS && canCreatePurchaseCase && canAddVehicleToBuy,
      {
        leftIcon: 'places/business_center',
        label: i18n.t('entity.businessCase.actions.addBusinessCase'),
        onClick: () =>
          openInNewTab(
            composePath(businessCaseRoutes.concept, {
              queryParams: {
                [queryParams.SOURCING_EXTERNAL_ID]: props.vehicle.sourcingVehicle.adId,
              },
              isSearchParamsPreserved: false,
            })
          ),
      }
    )
    .when(sourcingPage === SourcingPage.COMPARISON_PAGE, {
      leftIcon: 'action/search',
      label: i18n.t('general.actions.showDetail'),
      onClick: openDetail,
    })
    .when(isNotNil(props.vehicle.sourcingVehicle.url?.full), {
      leftIcon: 'action/open_in_new',
      label: `${i18n.t('general.actions.openResultOn')} ${adServerReadable}`,
      onClick: () =>
        isNotNil(props.vehicle.sourcingVehicle.url?.full) &&
        openInNewTab(props.vehicle.sourcingVehicle.url.full),
    })
    .add(
      createAdServerSearchMenuItem({
        label: i18n.t('general.actions.openResultMobileDe'),
        serverName: 'mobile_de',
        testId: 'mobileDeRelativeFilters',
      })
    )
    .when(countryAdServerName === 'sauto', () =>
      createAdServerSearchMenuItem({
        label: i18n.t('general.actions.openResultSauto'),
        serverName: 'sauto',
        testId: 'sautoRelativeFilters',
      })
    )
    .when(countryAdServerName === 'autoscout24', () =>
      createAdServerSearchMenuItem({
        label: i18n.t('general.actions.openResultAutoscout'),
        serverName: 'autoscout24',
        testId: 'autoscoutRelativeFilters',
      })
    )
    .when(countryAdServerName === 'otomoto', () =>
      createAdServerSearchMenuItem({
        label: i18n.t('general.actions.openResultOtomoto'),
        serverName: 'otomoto',
        testId: 'otomotoRelativeFilters',
      })
    )
    .when(countryAdServerName === 'autobazar_eu', () =>
      createAdServerSearchMenuItem({
        label: i18n.t('general.actions.openResultAutobazarEU'),
        serverName: 'autobazar_eu',
        testId: 'autobazarEuRelativeFilters',
      })
    )
    .when(countryAdServerName === 'autovit_ro', () =>
      createAdServerSearchMenuItem({
        label: i18n.t('general.actions.openResultAutovit'),
        serverName: 'autovit_ro',
        testId: 'autovitRelativeFilters',
      })
    )
    .when(countryAdServerName === 'lacentrale', () =>
      createAdServerSearchMenuItem({
        label: i18n.t('general.actions.openResultLacentrale'),
        serverName: 'lacentrale',
        testId: 'lacentraleRelativeFilters',
      })
    )
    .add({
      leftIcon: 'social/poll',
      label: i18n.t('entity.sourcing.labels.getPriceReport'),
      isLoading: isPriceReportCreating,
      onClick: createPriceReport,
    })
    .when(sourcingType === SourcingType.CLASSIFIEDS, {
      leftIcon: 'action/description',
      ...cebiaReportAction,
    })
    .when(
      sourcingType === SourcingType.CLASSIFIEDS &&
        isFeatureEnabled(featureFlags.SOURCING_CARVAGO_BUY),
      {
        leftIcon: 'custom/carvago',
        label: carvagoActionLabel,
        isDisabled: isCarvagoBuyDisabled,
        tooltip: carvagoBuyDisabledTooltipLabel
          ? {label: carvagoBuyDisabledTooltipLabel}
          : undefined,
        onClick: handleCarvagoBuyButtonClick,
      }
    )
    .when(sourcingPage === SourcingPage.COMPARISON_PAGE && !isVehicleHidden, {
      leftIcon: 'action/visibility_off',
      label: i18n.t('general.actions.hide'),
      onClick: handleHideButtonClick,
    })
    .when(sourcingPage === SourcingPage.COMPARISON_PAGE && isVehicleHidden, {
      leftIcon: 'action/visibility',
      label: i18n.t('general.actions.unhide'),
      onClick: handleHideButtonClick,
    })
    .when(sourcingPage === SourcingPage.COMPARISON_PAGE, {
      leftIcon: 'action/delete_outline',
      label: i18n.t('general.actions.removeFromList'),
      onClick: handleComparisonButtonClick,
    });

  const firstMenuItem = head(menuItems);

  const actions: Action[] = [
    {
      type: 'dropdown-iconButton',
      icon: 'navigation/more_horiz',
      priority: 'secondary',
      menuItems,
    },
  ];

  const menuActions = (
    <>
      <Show
        when={
          menuItems.length === 1 &&
          isNotNil(firstMenuItem?.leftIcon) &&
          isNotNil(firstMenuItem?.onClick)
            ? {item: firstMenuItem, icon: firstMenuItem.leftIcon, onClick: firstMenuItem.onClick}
            : null
        }
      >
        {(action) => (
          <SourcingActionButton
            hasUnifiedSurfaces={hasUnifiedSurfaces}
            size="small"
            label={action.item.label}
            icon={action.icon}
            severity={action.item.severity}
            isDisabled={action.item.isDisabled}
            onClick={action.onClick}
            data-testid={suffixTestId('actionButton', props)}
          />
        )}
      </Show>
      <Show when={menuItems.length > 1}>
        {hasUnifiedSurfaces ? (
          <Tooltip label={i18n.t('entity.orderRequest.actions.moreActions')}>
            <Actions size="small" actions={actions} data-testid={suffixTestId('actions', props)} />
          </Tooltip>
        ) : (
          <Actions size="small" actions={actions} data-testid={suffixTestId('actions', props)} />
        )}
      </Show>
    </>
  );

  return (
    <HStack spacing={1} align="flex-start">
      <SourcingActionButton
        hasUnifiedSurfaces={hasUnifiedSurfaces}
        size="small"
        label={i18n.t(
          props.vehicle?.favourite
            ? 'general.actions.removeFromFavourites'
            : 'general.actions.addToFavourites'
        )}
        severity={props.vehicle?.favourite ? 'warning' : undefined}
        icon="toggle/star"
        data-testid={suffixTestId('favouriteButton', props)}
        onClick={handleFavouriteButtonClick}
      />

      <SourcingActionButton
        hasUnifiedSurfaces={hasUnifiedSurfaces}
        size="small"
        label={i18n.t(
          props.vehicle?.inCart ? 'general.actions.removeFromBasket' : 'general.actions.addToBasket'
        )}
        severity={props.vehicle?.inCart ? 'informational' : undefined}
        icon="action/shopping_cart"
        data-testid={suffixTestId('cartButton', props)}
        onClick={handleShopButtonClick}
      />

      <Hide when={sourcingPage === SourcingPage.COMPARISON_PAGE}>
        <SourcingActionButton
          hasUnifiedSurfaces={hasUnifiedSurfaces}
          size="small"
          label={i18n.t(
            props.vehicle?.comparison
              ? 'general.actions.removeFromList'
              : 'general.actions.addToComparison'
          )}
          severity={props.vehicle?.comparison ? 'success' : undefined}
          icon="action/compare_arrows"
          data-testid={suffixTestId('comparisonButton', props)}
          onClick={handleComparisonButtonClick}
        />

        <SourcingActionButton
          hasUnifiedSurfaces={hasUnifiedSurfaces}
          size="small"
          label={i18n.t(props.vehicle?.hidden ? 'general.actions.unhide' : 'general.actions.hide')}
          icon={props.vehicle?.hidden ? 'action/visibility' : 'action/visibility_off'}
          severity={props.vehicle?.hidden ? 'danger' : undefined}
          data-testid={suffixTestId('unhideButton', props)}
          onClick={handleHideButtonClick}
        />
      </Hide>

      <Show when={!hasUnifiedSurfaces}>{menuActions}</Show>

      <Show
        when={sourcingType === SourcingType.CLASSIFIEDS}
        whenFeatureEnabled={featureFlags.SOURCING_CARVAGO_BUY}
      >
        <CarvagoActionButton
          hasUnifiedSurfaces={hasUnifiedSurfaces}
          isInList={isCarvagoBuyAlreadyRequested}
          label={carvagoActionLabel}
          tooltip={carvagoBuyDisabledTooltipLabel}
          isDisabled={isCarvagoBuyDisabled}
          data-testid={suffixTestId('carvagoBuyButton', props)}
          onClick={handleCarvagoBuyButtonClick}
        />
      </Show>

      <Show when={hasUnifiedSurfaces}>{menuActions}</Show>

      <Hide when={sourcingPage === SourcingPage.COMPARISON_PAGE || isCurrentDetailPage}>
        <Link to={getDetailPath()} data-testid={suffixTestId('detailLink', props)}>
          <SourcingActionButton
            hasUnifiedSurfaces={hasUnifiedSurfaces}
            size="small"
            priority="primary"
            label={i18n.t('general.actions.showDetail')}
            icon="hardware/keyboard_arrow_right"
            onClick={rememberOrigin}
            data-testid={suffixTestId('detailButton', props)}
          />
        </Link>
      </Hide>

      <ConfirmDialog
        text={i18n.t('entity.vehicle.labels.reallyHide')}
        onConfirm={handleConfirmHide}
        onClose={close}
        isOpen={isOpen}
        data-testid={testIds.sourcing.classifieds('actionButtonsVehicle-hideConfirmDialog')}
      />

      <SourcingCarvagoBuyDialog
        isOpen={isCarvagoBuyOpen}
        onClose={closeCarvagoBuy}
        onConfirm={handleConfirmCarvagoBuy}
        data-testid={testIds.sourcing.classifieds('actionButtonsVehicle-carvagoBuyDialog')}
      />

      {cebiaConfirmDialog}

      {relativeFiltersPresetEditor.presetEditorDialog}
    </HStack>
  );
}
